import React, { useEffect, useMemo, useRef } from 'react';
import Plotly from 'plotly.js-dist';
import {
  backtestMap,
  BacktestTokenKey,
  BacktestWindow,
} from '../data/backtest';

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const formatDateLabel = (iso?: string | null) => {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const hh = String(parsed.getHours()).padStart(2, '0');
  const mi = String(parsed.getMinutes()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${mi}`;
};

const formatStatValue = (value: string | number | null) => {
  if (typeof value === 'number') {
    return NUMBER_FORMATTER.format(value);
  }
  if (typeof value === 'string') {
    const formatted = formatDateLabel(value);
    return formatted || value;
  }
  return '-';
};

// 토큰별 색상 설정
const TOKEN_COLORS: Record<BacktestTokenKey, string> = {
  pigeon: '#4ade80',   // GOOD 1
  grizzly: '#22c55e',  // GOOD 2
  shark: '#16a34a',    // GOOD 3
  zap: '#f87171',      // BAD 1
  chonkachu: '#ef4444', // BAD 2
  nipah: '#dc2626',    // BAD 3
  baszucki: '#b91c1c', // BAD 4
};

const ALL_TOKENS: BacktestTokenKey[] = ['pigeon', 'grizzly', 'shark', 'zap', 'chonkachu', 'nipah', 'baszucki'];

const METRICS_TO_RENDER: Array<{ key: keyof BacktestWindow; label: string; isRatio?: boolean }> = [
  { key: 'tweetCount', label: 'Tweet Count' },
  { key: 'communityTweetCount', label: 'Community Tweet Count' },
  { key: 'uniqueAuthors', label: 'Unique Authors' },
  { key: 'uniqueAuthorsFollowersOver10000', label: 'Unique Authors Followers > 10k' },
  { key: 'uniqueAuthorsFollowersOver50000', label: 'Unique Authors Followers > 50k' },
  { key: 'uniqueAuthorsFollowersOver100000', label: 'Unique Authors Followers > 100k' },
  { key: 'uniqueAuthorsFollowersOver150000', label: 'Unique Authors Followers > 150k' },
  { key: 'newbieAuthors', label: 'Newbie Authors' },
  { key: 'blueVerifiedAuthors', label: 'Blue Verified Authors' },
  { key: 'lowFollowerAuthors', label: 'Low Follower Authors' },
  { key: 'adminTweetCount', label: 'Admin Tweet Count' },
  { key: 'modTweetCount', label: 'Mod Tweet Count' },
  { key: 'modAuthorCount', label: 'Mod Author Count' },
  { key: 'newbieRatio', label: 'Newbie Ratio', isRatio: true },
  { key: 'lowFollowerRatio', label: 'Low Follower Ratio', isRatio: true },
  { key: 'over10000Ratio', label: 'Over 10k Ratio', isRatio: true },
  { key: 'over50000Ratio', label: 'Over 50k Ratio', isRatio: true },
  { key: 'over100000Ratio', label: 'Over 100k Ratio', isRatio: true },
  { key: 'over150000Ratio', label: 'Over 150k Ratio', isRatio: true },
  { key: 'blueVerifiedRatio', label: 'Blue Verified Ratio', isRatio: true },
  { key: 'adminTweetRatio', label: 'Admin Tweet Ratio', isRatio: true },
  { key: 'modTweetRatio', label: 'Mod Tweet Ratio', isRatio: true },
];

const statsList = [
  { label: 'Close 변화 윈도우', key: 'largestCloseDiffWindowStart' },
  { label: 'Volume 변화 윈도우', key: 'largestVolumeDiffWindowStart' },
  { label: 'Not-buy Close', key: 'notBuyDecisionClose' },
  { label: 'Not-buy Volume', key: 'notBuyDecisionVolume' },
  { label: 'Admin 트윗 총합', key: 'adminTotalTweetsBeforeDecision' },
  { label: 'Mod 트윗 총합', key: 'modTotalTweetsBeforeDecision' },
  { label: 'Blue Verified', key: 'blueVerifiedAuthorsBeforeDecision' },
  { label: 'Not-buy 시간', key: 'notBuyDecisionTime' },
];

const MultiTokenMetricChart: React.FC<{
  metricKey: keyof BacktestWindow;
  label: string;
  isRatio?: boolean;
}> = ({ metricKey, label, isRatio }) => {
  const plotRef = useRef<HTMLDivElement>(null);

  const plotData = useMemo(() => {
    return ALL_TOKENS.map((tokenKey) => {
      const entry = backtestMap[tokenKey];
      if (!entry) return null;

      const trimmed = entry.data.windows.slice(-60);
      if (trimmed.length === 0) return null;

      const timestamps = trimmed.map((win) => new Date(win.windowStart).getTime());
      const minTs = Math.min(...timestamps);
      const maxTs = Math.max(...timestamps);
      const tsRange = maxTs - minTs || 1;

      return {
        x: timestamps.map((ts) => ((ts - minTs) / tsRange) * 100),
        y: trimmed.map((win) => {
          const val = win[metricKey];
          return typeof val === 'number' ? val : Number(val) || 0;
        }),
        text: trimmed.map((win) => formatDateLabel(win.windowStart)), // 호버 시 실제 시간을 보여주기 위해 추가
        hovertemplate: `<b>${entry.label}</b><br>Time: %{text}<br>Value: %{y}<extra></extra>`,
        name: entry.label,
        mode: 'lines',
        line: {
          color: TOKEN_COLORS[tokenKey],
          width: 2,
        },
      };
    }).filter(Boolean);
  }, [metricKey]);

  const layout = useMemo(() => ({
    title: {
      text: label,
      font: { color: '#FFFFFF', size: 14 },
      x: 0.05,
    },
    margin: { l: 50, r: 30, b: 40, t: 50 },
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    font: { color: '#FFFFFF', size: 11 },
    xaxis: {
      title: {
        text: 'Relative Timeline (%)',
        font: { color: '#9CA3AF', size: 10 },
      },
      range: [0, 100],
      showgrid: true,
      gridcolor: '#374151',
      tickfont: { color: '#9CA3AF' },
      tickformat: '.0f',
      ticksuffix: '%',
    },
    yaxis: {
      showgrid: true,
      gridcolor: '#374151',
      tickfont: { color: '#9CA3AF' },
      tickformat: isRatio ? '.1%' : undefined,
    },
    legend: {
      orientation: 'h',
      y: -0.2,
      font: { color: '#9CA3AF', size: 10 },
    },
    hovermode: 'x unified',
  }), [label, isRatio]);

  const config = useMemo(() => ({
    displayModeBar: false,
    responsive: true,
  }), []);

  useEffect(() => {
    const currentPlotRef = plotRef.current;
    if (!currentPlotRef || plotData.length === 0) return;

    Plotly.newPlot(currentPlotRef, plotData as any, layout as any, config);

    return () => {
      if (currentPlotRef) {
        Plotly.purge(currentPlotRef);
      }
    };
  }, [plotData, layout, config]);

  return (
    <div className="rounded-2xl border border-gray-800 bg-gray-950/40 p-4 shadow-xl">
      <div ref={plotRef} className="h-[350px] w-full" />
    </div>
  );
};

const StatsDashboard: React.FC = () => {
  return (
    <div className="grid grid-cols-1 gap-6 mb-8">
      <h2 className="text-2xl font-bold text-white mb-2">Stats Comparison</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm text-gray-400">
          <thead className="text-xs uppercase text-gray-500 bg-gray-900/50">
            <tr>
              <th className="px-4 py-3">Metric</th>
              {ALL_TOKENS.map((tokenKey) => (
                <th key={tokenKey} className="px-4 py-3" style={{ color: TOKEN_COLORS[tokenKey] }}>
                  {backtestMap[tokenKey]?.label || tokenKey}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {statsList.map((stat) => (
              <tr key={stat.key} className="hover:bg-white/5 transition-colors">
                <td className="px-4 py-3 font-medium text-gray-300">{stat.label}</td>
                {ALL_TOKENS.map((tokenKey) => {
                  const val = (backtestMap[tokenKey]?.data.stats as any)?.[stat.key];
                  return (
                    <td key={tokenKey} className="px-4 py-3 whitespace-nowrap">
                      {formatStatValue(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const BacktestPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black px-6 py-10 text-white">
      <div className="mx-auto max-w-6xl">
        <header className="mb-12">
          <h1 className="text-4xl font-bold text-white tracking-tight">Backtest Analytics</h1>
        </header>

        <StatsDashboard />

        <div className="flex flex-col gap-10" style={{ overflowY: 'auto', height: 'calc(100vh - 700px)' }}>
          <h2 className="text-2xl font-bold text-white">Metrics Timeline</h2>
          {METRICS_TO_RENDER.map((metric) => (
            <MultiTokenMetricChart
              key={metric.key}
              metricKey={metric.key}
              label={metric.label}
              isRatio={metric.isRatio}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BacktestPage;
